import React, { useEffect, useState } from 'react';
import { fetchOpenMatches, resolveMatch } from '../../services/leadImport';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import { AlertTriangle, Building2, CheckCircle2, GitMerge, Loader2, Trash2, X } from 'lucide-react';

/**
 * Review der offenen Entity-Resolution-Faelle (Dedupe-Ebene 3, ERP-Plan 5).
 *
 * Aufbau nach dem Muster von ImportMergeDialog: eine Gegenueberstellung
 * „Beobachtung ↔ Bestand" und eine Entscheidung je Fall — hier allerdings
 * nicht feldweise, denn zu entscheiden ist nur EINES: ist das dieselbe Firma?
 *
 * Warum es diesen Schritt ueberhaupt gibt: der Import ordnet nur oberhalb von
 * `AUTO_ZUORDNEN_AB` selbstaendig zu. Eine falsche automatische Zuordnung
 * verschmilzt zwei Firmen still, und das faellt erst auf, wenn jemand den
 * falschen Ansprechpartner anruft. Alles darunter kommt deshalb hierher.
 */
interface Props {
    onClose: () => void;
    /** Nach Entscheidungen: der Aufrufer laedt Leads und Badge neu. */
    onResolved: () => void;
}

/** Rohzeile aus fetchOpenMatches (mit eingebetteten Bezugsobjekten). */
interface MatchRow {
    id: string;
    raw_id: string;
    confidence?: number | null;
    matched_lead_id?: string | null;
    matched_customer_id?: string | null;
    leads_raw?: { source?: string; source_record_id?: string; payload?: Record<string, any> } | null;
    leads?: { name?: string; zip?: string; city?: string } | null;
    customers?: { name?: string; zip?: string; city?: string } | null;
}

const feldwert = (payload: Record<string, any> | undefined, ...keys: string[]): string => {
    for (const k of keys) {
        const v = payload?.[k];
        if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
};

/** Konfidenz als Prozent — im Review zaehlt die Groessenordnung, nicht die dritte Stelle. */
const prozent = (c?: number | null): string =>
    c == null ? '—' : `${Math.round(Number(c) * 100)} %`;

export const LeadMatchReviewModal: React.FC<Props> = ({ onClose, onResolved }) => {
    const [rows, setRows] = useState<MatchRow[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    // Id des gerade entschiedenen Falls — sperrt nur dessen Knoepfe.
    const [arbeitet, setArbeitet] = useState<string | null>(null);
    const [erledigt, setErledigt] = useState(0);

    const laden = async () => {
        setLaedt(true);
        setFehler(null);
        try {
            setRows((await fetchOpenMatches()) as MatchRow[]);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    };

    useEffect(() => {
        void laden();
    }, []);

    const entscheiden = async (row: MatchRow, entscheidung: 'manuell_bestaetigt' | 'verworfen') => {
        setArbeitet(row.id);
        setFehler(null);
        try {
            await resolveMatch(row.id, entscheidung);
            // Nur die entschiedene Zeile entfernen statt alles neu zu laden:
            // sonst springt die Liste unter dem Finger weg.
            setRows((prev) => prev.filter((r) => r.id !== row.id));
            setErledigt((n) => n + 1);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setArbeitet(null);
        }
    };

    const schliessen = () => {
        if (erledigt > 0) onResolved();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-amber-500/20 p-2 rounded-xl border border-amber-500/30">
                            <GitMerge className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                            <h3 className="font-bold">Zuordnungen prüfen</h3>
                            <p className="text-xs text-slate-400">
                                Gefundene Firmen, bei denen die Automatik sich nicht sicher war.
                            </p>
                        </div>
                    </div>
                    <button onClick={schliessen} className="hover:bg-white/10 p-2 rounded-full transition-all">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                    {fehler && (
                        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{fehler}
                        </div>
                    )}

                    {laedt ? (
                        <p className="text-sm text-slate-500 py-10 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                            Offene Zuordnungen werden geladen…
                        </p>
                    ) : rows.length === 0 ? (
                        <div className="py-12 text-center text-slate-400">
                            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
                            <p className="font-bold text-slate-600">Nichts zu prüfen</p>
                            <p className="text-xs">
                                {erledigt > 0
                                    ? `${erledigt} Zuordnung${erledigt === 1 ? '' : 'en'} in dieser Sitzung entschieden.`
                                    : 'Alle Beobachtungen sind eindeutig zugeordnet.'}
                            </p>
                        </div>
                    ) : (
                        rows.map((row) => {
                            const payload = row.leads_raw?.payload;
                            const bestand = row.customers ?? row.leads;
                            const bestandArt = row.customers ? 'Bestandskunde' : 'vorhandener Lead';
                            const beobachtungName = feldwert(payload, 'name', 'firma', 'company', 'titel');
                            const beobachtungOrt = [
                                feldwert(payload, 'zip', 'plz'),
                                feldwert(payload, 'city', 'ort'),
                            ].filter(Boolean).join(' ');
                            const busy = arbeitet === row.id;

                            return (
                                <article key={row.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                                    <div className="bg-slate-50 px-4 py-2 flex items-center justify-between text-xs">
                                        <span className="text-slate-500 font-mono truncate">
                                            {row.leads_raw?.source} · {row.leads_raw?.source_record_id}
                                        </span>
                                        <span className="font-bold text-amber-700 shrink-0">
                                            Übereinstimmung {prozent(row.confidence)}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                                        <div className="p-4">
                                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                                Gefunden
                                            </p>
                                            <p className="font-bold text-slate-800 flex items-center gap-1.5">
                                                <Building2 className="w-4 h-4 text-slate-300 shrink-0" />
                                                {beobachtungName || <span className="text-slate-300">ohne Namen</span>}
                                            </p>
                                            <p className="text-sm text-slate-500">{beobachtungOrt || '—'}</p>
                                            {feldwert(payload, 'website', 'url') && (
                                                <p className="text-xs text-slate-400 truncate">{feldwert(payload, 'website', 'url')}</p>
                                            )}
                                        </div>

                                        <div className="p-4">
                                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                                {bestandArt}
                                            </p>
                                            <p className="font-bold text-slate-800 flex items-center gap-1.5">
                                                <Building2 className="w-4 h-4 text-slate-300 shrink-0" />
                                                {bestand?.name || <span className="text-slate-300">nicht mehr vorhanden</span>}
                                            </p>
                                            <p className="text-sm text-slate-500">
                                                {[bestand?.zip, bestand?.city].filter(Boolean).join(' ') || '—'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-white px-4 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-xs text-slate-500">
                                            Dieselbe Firma? Dann bestätigen — es entsteht kein zweiter Datensatz.
                                        </p>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="secondary"
                                                onClick={() => void entscheiden(row, 'verworfen')}
                                                disabled={busy}
                                                icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                title="Kein Bezug — die Beobachtung wird nicht zugeordnet"
                                            >
                                                Kein Bezug
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={() => void entscheiden(row, 'manuell_bestaetigt')}
                                                disabled={busy || !bestand?.name}
                                                icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                            >
                                                Ist dieselbe Firma
                                            </Button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-between items-center shrink-0">
                    <span className="text-xs text-slate-500">
                        {rows.length} offen{erledigt > 0 ? ` · ${erledigt} entschieden` : ''}
                    </span>
                    <Button variant="ghost" onClick={schliessen}>Schließen</Button>
                </div>
            </div>
        </div>
    );
};
